import React, { useState, useEffect } from 'react';
import { Card, Typography, Tabs } from 'antd';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { formatPriceToman } from '../../../utils/formatPriceToman';
import { financialApi } from '../../../utils/financialApi';
import { extractApiUserMessage } from '../../../utils/api';
import dayjs from 'dayjs';
import jalaliday from 'jalali-dayjs';

dayjs.extend(jalaliday);

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const CashflowForecast = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCashflow = async () => {
      try {
        setLoading(true);
        const res = await financialApi.getCashflowForecast();
        const cashflowData = res?.cashflow_forecast || res?.data?.cashflow_forecast || res;
        setData(cashflowData);
      } catch (error) {
        console.error('Failed to fetch cashflow forecast:', extractApiUserMessage(error));
      } finally {
        setLoading(false);
      }
    };
    fetchCashflow();
  }, []);

  if (!data && !loading) return null;

  const forecastWindows = data?.forecast_windows || {};
  
  // Format data for chart
  const formatChartData = (windowKey, windowData) => {
    if (!windowData) return [];
    
    return [
      {
        name: 'ورودی (چک/اقساط)',
        'چک‌های دریافتی': windowData.inflow_cheques || 0,
        'اقساط': windowData.inflow_installments || 0,
        total: windowData.total_inflow || 0
      },
      {
        name: 'خروجی پیش‌بینی شده',
        'خروجی': windowData.total_outflow || 0,
        total: windowData.total_outflow || 0
      }
    ];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div dir="rtl" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 rounded-lg shadow-lg">
          <p className="font-bold text-gray-800 dark:text-gray-200 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 mb-1 text-sm">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></span>
              <span className="text-gray-600 dark:text-gray-400">{entry.name}:</span>
              <span className="font-medium">{formatPriceToman(entry.value)}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderForecastTab = (windowKey, label) => {
    const windowData = forecastWindows[windowKey];
    if (!windowData) return <div className="p-4 text-center text-gray-500">داده‌ای برای این بازه موجود نیست</div>;

    const chartData = formatChartData(windowKey, windowData);
    const isPositiveChange = (windowData.net_period_change || 0) >= 0;

    return (
      <div className="pt-2">
        <div className="flex flex-wrap justify-between items-center mb-6 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
          <div>
            <Text className="text-gray-500 block mb-1 text-xs">تغییر خالص دوره</Text>
            <Title level={5} className={`m-0 ${isPositiveChange ? 'text-emerald-500' : 'text-red-500'}`}>
              {isPositiveChange ? '+' : ''}{formatPriceToman(windowData.net_period_change || 0)}
            </Title>
          </div>
          <div className="text-left mt-2 sm:mt-0">
            <Text className="text-gray-500 block mb-1 text-xs">موجودی پیش‌بینی شده در پایان دوره</Text>
            <Title level={5} className="m-0 text-blue-600 dark:text-blue-400">
              {formatPriceToman(windowData.projected_liquid_position || 0)}
            </Title>
          </div>
        </div>

        <div className="h-[250px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis orientation="right" 
                tickFormatter={(value) => {
                  if (value === 0) return '0';
                  return `${value / 1000000}M`;
                }} 
                axisLine={false} 
                tickLine={false}
                tick={{ fill: '#6b7280', fontSize: 11 }}
                width={60}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              
              {/* Using stacked bars for inflow components, single bar for outflow */}
              <Bar dataKey="چک‌های دریافتی" stackId="a" fill="#3b82f6" radius={[0, 0, 4, 4]} maxBarSize={60} />
              <Bar dataKey="اقساط" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={60} />
              <Bar dataKey="خروجی" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={60} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <Card 
      title="پیش‌بینی جریان نقدینگی (Cashflow Forecast)" 
      loading={loading}
      bordered={false} 
      className="shadow-sm dark:bg-gray-800 rounded-2xl h-full"
    >
      <Tabs defaultActiveKey="30_days" className="finance-tabs">
        <TabPane tab="۳۰ روز آینده" key="30_days">
          {renderForecastTab('30_days', '۳۰ روز')}
        </TabPane>
        <TabPane tab="۶۰ روز آینده" key="60_days">
          {renderForecastTab('60_days', '۶۰ روز')}
        </TabPane>
        <TabPane tab="۹۰ روز آینده" key="90_days">
          {renderForecastTab('90_days', '۹۰ روز')}
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default CashflowForecast;
